import React, { useEffect, useState } from "react";
import { Grid, Button, Card, CardContent, Typography, Box, CircularProgress } from "@mui/material";
import { Line, Doughnut, Bar } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    ArcElement,
    BarElement,
    Tooltip,
    Legend,
} from "chart.js";
import { getInvoiceReportAction } from "../../Actions/ReportsAction";
import CustomSnackbar from "../Common/snackBar";
import FilterDrawer from "../../components/DataTable/FilterDrawer";
import { TableFilte } from "../../Assets/svg/svgicon";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, ArcElement, BarElement, Tooltip, Legend);

const drawerFilters = [
    {
        name: "portal",
        label: "Portal",
        type: "select",
        defaultValue: "all",
        options: [
            { label: "All Portals", value: "all" },
            { label: "Tripola", value: "tripola" },
            { label: "PayCentral", value: "paycentral" },
        ],
    },
    {
        name: "status",
        label: "Status",
        type: "status",
        defaultValue: "all",
        options: [
            { label: "All Status", value: "all" },
            { label: "Paid", value: "paid" },
            { label: "Pending", value: "pending" },
        ],
    },
    {
        name: "dateRange",
        label: "Date Range",
        type: "dateRange",
        placeholder: "Select date range",
    },
];

const InvoiceReports = () => {
    const [loading, setLoading] = useState(false);

    const getDefaultDateRange = () => {
        const to = new Date();
        const from = new Date();
        from.setMonth(from.getMonth() - 6);
        return { from, to };
    };

    const [filterValues, setFilterValues] = useState({
        portal: "all",
        status: "all",
        dateRange: getDefaultDateRange(),
    });

    const [filterAnchorEl, setFilterAnchorEl] = useState(null);

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const [summary, setSummary] = useState({
        totalInvoices: 0,
        paidInvoices: 0,
        pendingInvoices: 0,
        totalAmount: 0,
    });

    const [chartData, setChartData] = useState({
        trend: [],
        status: { paid: 0, pending: 0 },
        productAmount: { tripola: 0, paycentral: 0 },
    });

    const buildApiFilters = (fv) => ({
        product: fv.portal === "all" ? "" : fv.portal,
        status: fv.status === "all" ? "" : fv.status,
        startDate: fv.dateRange?.from ? fv.dateRange.from.toISOString().split("T")[0] : "",
        endDate: fv.dateRange?.to ? fv.dateRange.to.toISOString().split("T")[0] : "",
    });

    const fetchInvoiceReport = async (fv = filterValues) => {
        try {
            setLoading(true);
            const response = await getInvoiceReportAction(buildApiFilters(fv));
            if (response?.status === 200) {
                setSummary(response.data.summary);
                setChartData(response.data.charts);
            }
        } catch (error) {
            setSnackbar({
                open: true,
                message: error?.response?.data?.message || "Something went wrong",
                severity: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleFilterApply = (newValues) => {
        setFilterValues(newValues);
        fetchInvoiceReport(newValues);
    };

    const handleFilterReset = () => {
        const resetValues = { portal: "all", status: "all", dateRange: getDefaultDateRange() };
        setFilterValues(resetValues);
        fetchInvoiceReport(resetValues);
    };

    useEffect(() => {
        fetchInvoiceReport();
    }, []);

    const hasAppliedFilters =
        filterValues.portal !== "all" ||
        filterValues.status !== "all";

    const trendData = {
        labels: chartData?.trend?.map((item) => item.month) || [],
        datasets: [
            {
                label: "Invoice Amount",
                data: chartData?.trend?.map((item) => item.amount) || [],
                borderColor: "#22419e",
                backgroundColor: "rgba(34,65,158,0.15)",
                pointBackgroundColor: "#22419e",
                pointBorderColor: "#22419e",
                pointRadius: 5,
                tension: 0.4,
                borderWidth: 3,
                fill: true,
            },
        ],
    };

    const statusChartData = {
        labels: ["Paid", "Pending"],
        datasets: [
            {
                data: [
                    chartData?.status?.paid || 0,
                    chartData?.status?.pending || 0,
                ],
                backgroundColor: ["#16a34a", "#f59e0b"],
                borderWidth: 1,
            },
        ],
    };

    const productChartData = {
        labels: ["Tripola", "PayCentral"],
        datasets: [
            {
                label: "Invoice Amount",
                data: [
                    chartData?.productAmount?.tripola || 0,
                    chartData?.productAmount?.paycentral || 0,
                ],
                backgroundColor: ["#22419e", "#16a34a"],
                borderWidth: 1,
            },
        ],
    };

    return (
        <>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <div className="card border-rounded-0 fiter-card">
                        <div className="card-body p-5 pb-0">
                            <div className="row align-items-center mb-5">
                                <div className="col-auto">
                                    <h2 className="chart-title mb-0">
                                        Invoice Report
                                    </h2>
                                </div>

                                <div className="col-auto ms-auto d-flex gap-2">
                                    <Button
                                        variant="outlined"
                                        onClick={(e) => setFilterAnchorEl(e.currentTarget)}
                                        startIcon={<TableFilte />}
                                        className={`dt-filter-btn ${hasAppliedFilters ? "active" : ""}`}
                                    >
                                        Filters
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </Grid>

                <Grid item xs={12} md={3}>
                    <Card sx={{ bgcolor: "#EEF4FF", boxShadow: 2 }}>
                        <CardContent>
                            <Typography variant="h6">Total Invoices</Typography>
                            <Typography variant="h4">{summary.totalInvoices}</Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={3}>
                    <Card sx={{ bgcolor: "#e9f8f0", boxShadow: 2 }}>
                        <CardContent>
                            <Typography variant="h6">Paid Invoices</Typography>
                            <Typography variant="h4">{summary.paidInvoices}</Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={3}>
                    <Card sx={{ bgcolor: "#f8f1f3", boxShadow: 2 }}>
                        <CardContent>
                            <Typography variant="h6">Pending Invoices</Typography>
                            <Typography variant="h4">{summary.pendingInvoices}</Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={3}>
                    <Card sx={{ bgcolor: "#FFF7E6", boxShadow: 2 }}>
                        <CardContent>
                            <Typography variant="h6">Total Amount</Typography>
                            <Typography variant="h4">
                                {Number(summary.totalAmount || 0).toLocaleString()}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid container spacing={3} mt={1} sx={{ width: "100%", marginLeft: 0 }}>
                    <Grid item xs={12} md={8} sx={{ display: "flex" }}>
                        <Card sx={{ borderRadius: 3, height: "100%", width: "100%", overflow: "hidden" }}>
                            <CardContent>
                                <Typography variant="h6" fontWeight={700} mb={3}>
                                    Invoice Trend
                                </Typography>
                                {loading ? (
                                    <Box display="flex" justifyContent="center" py={8}>
                                        <CircularProgress />
                                    </Box>
                                ) : (
                                    <Box height={350}>
                                        <Line
                                            data={trendData}
                                            options={{ responsive: true, maintainAspectRatio: false }}
                                        />
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={4} sx={{ display: "flex" }}>
                        <Card sx={{ borderRadius: 3, height: "100%", width: "100%", overflow: "hidden" }}>
                            <CardContent>
                                <Typography variant="h6" fontWeight={700} mb={3}>
                                    Invoice Status
                                </Typography>
                                <Box height={350}>
                                    <Doughnut
                                        data={statusChartData}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: { legend: { position: "bottom" } },
                                        }}
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" fontWeight={700} mb={3}>
                                    Invoice Amount By Product
                                </Typography>
                                <Box height={350}>
                                    <Bar
                                        data={productChartData}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: { legend: { display: false } },
                                        }}
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Grid>

            {/* Filter Drawer */}
            <FilterDrawer
                open={Boolean(filterAnchorEl)}
                anchorEl={filterAnchorEl}
                onClose={() => setFilterAnchorEl(null)}
                filters={drawerFilters}
                filterValues={filterValues}
                onApply={handleFilterApply}
                onReset={handleFilterReset}
            />

            <CustomSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={handleCloseSnackbar}
            />
        </>
    );
};

export default InvoiceReports;
